import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Circle, Loader2, Maximize2, Minimize2, Radio, Volume2, VolumeX, WifiOff, Eye, EyeOff, Tv } from "lucide-react";
import ProgramCanvas from "@/components/studio/ProgramCanvas";
import { useRtkMeeting, useRoster, useStableSpeaker } from "@/hooks/useRtkMeeting";
import { useShowSocket } from "@/hooks/useShowSocket";
import { useElapsed, useClock } from "@/hooks/useElapsed";
import { studioApi } from "@/lib/studioApi";

/**
 * Room monitor — /monitor/{showId}/{key}. Opened on the venue TVs (or the host's laptop): joins
 * the RealtimeKit room as a hidden viewer and renders the program exactly as the recorder does,
 * ~0.3 s behind real life instead of the 15–30 s of the public HLS broadcast. Muted by default
 * (the room already hears itself), keeps the screen awake, and never stays black:
 *   • room link lost / media transport dead / no frames for 20 s  -> rebuild the connection
 *   • still no picture after repeated tries while the show is streaming -> show the public
 *     broadcast instead (delayed, but a picture) and keep probing the room link to switch back
 *   • every screen reports its health to the console ("TV 2: no picture") and to a 14-day log.
 */
const NO_FRAMES_MS = 20000;      // joined, sources on air, nothing painted -> rebuild
const TRANSPORT_DEAD_MS = 15000; // recv transport failed/disconnected this long -> rebuild
const FALLBACK_AFTER = 2;        // consecutive failed rebuilds before showing the broadcast
const PROBE_EVERY_MS = 90000;    // while on the broadcast, try the room link this often
const PROBE_FOR_MS = 30000;      // ...for this long
const BEACON_MS = 20000;

const backoff = (attempt) => Math.min(30000, 2000 * 2 ** Math.min(attempt, 4));

function monitorId() {
  let id = sessionStorage.getItem("fc_monitor_id");
  if (!id) { id = `mon_${Math.random().toString(36).slice(2, 8)}`; sessionStorage.setItem("fc_monitor_id", id); }
  return id;
}

export default function RoomMonitor() {
  const { showId, key } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const prev = { bg: document.body.style.background, overflow: document.body.style.overflow, cursor: document.body.style.cursor };
    document.body.style.background = "#000";
    document.body.style.overflow = "hidden";
    return () => { document.body.style.background = prev.bg; document.body.style.overflow = prev.overflow; document.body.style.cursor = prev.cursor; };
  }, []);

  useEffect(() => {
    studioApi.monitorData(showId, key).then(setInfo).catch((e) => setError(e?.response?.status === 404 ? "This monitor link isn't valid anymore. Ask the producer for a new one." : "Could not load the show"));
  }, [showId, key]);

  if (error) return <Notice title="Room monitor" text={error} testId="monitor-error" />;
  if (!info) return <Notice title="Room monitor" text="Connecting…" spinner testId="monitor-loading" />;
  if (info.status === "ended") return <Notice title={info.title} text="This show has ended." testId="monitor-ended" />;
  return <MonitorLive showId={showId} monitorKey={key} initial={info} />;
}

function Notice({ title, text, spinner, testId }) {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black text-center text-white" data-testid={testId}>
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/50">{title}</p>
        <p className="mt-3 flex items-center justify-center gap-2 text-lg">{spinner && <Loader2 className="h-5 w-5 animate-spin" />}{text}</p>
      </div>
    </div>
  );
}

/** ON AIR / REC badges + wall clock for the host, top-left, always visible (toggle in the controls). */
function Tally({ show }) {
  const live = show?.livestream || {};
  const rec = show?.recording || {};
  const liveOn = ["starting", "live"].includes(live.status);
  const recOn = ["starting", "recording"].includes(rec.status);
  const liveT = useElapsed(live.started_at, liveOn);
  const recT = useElapsed(rec.started_at, recOn);
  const clock = useClock();
  return (
    <div className="absolute left-5 top-5 z-10 flex items-center gap-2 font-mono" data-testid="monitor-tally">
      {liveOn && (
        <span className="inline-flex items-center gap-2 rounded-md bg-[#c9772e] px-3 py-1.5 text-sm font-bold uppercase tracking-widest text-white shadow-lg" data-testid="tally-live">
          <Radio className="h-4 w-4 animate-pulse" /> On air <span className="font-normal opacity-90">{liveT}</span>
        </span>
      )}
      {recOn && (
        <span className="inline-flex items-center gap-2 rounded-md bg-red-600 px-3 py-1.5 text-sm font-bold uppercase tracking-widest text-white shadow-lg" data-testid="tally-rec">
          <Circle className="h-3 w-3 animate-pulse fill-current" /> Rec <span className="font-normal opacity-90">{recT}</span>
        </span>
      )}
      {!liveOn && !recOn && (
        <span className="inline-flex items-center rounded-md bg-black/60 px-3 py-1.5 text-sm font-semibold uppercase tracking-widest text-white/70 backdrop-blur" data-testid="tally-off">Off air</span>
      )}
      <span className="rounded-md bg-black/60 px-3 py-1.5 text-sm font-semibold tabular-nums text-white/90 backdrop-blur" data-testid="tally-clock">{clock}</span>
    </div>
  );
}

/** Owns the connection life-cycle: token, rebuilds with backoff, broadcast fallback, health beacons. */
function MonitorLive({ showId, monitorKey, initial }) {
  const { show: liveShow } = useShowSocket(showId, { monitorKey });
  const show = liveShow || initial;
  const mid = useMemo(() => monitorId(), []);
  const label = useMemo(() => new URLSearchParams(window.location.search).get("label") || `Screen ${mid.slice(-4).toUpperCase()}`, [mid]);

  const [token, setToken] = useState(null);
  const [tokenError, setTokenError] = useState(null);
  const [tokenGen, setTokenGen] = useState(0);   // bump -> fetch a fresh token
  const [session, setSession] = useState(0);     // bump -> new client, same token
  const [attempt, setAttempt] = useState(0);     // consecutive failures (backoff + fallback)
  const [pending, setPending] = useState(null);  // a rebuild is scheduled: { reason }
  const [picture, setPicture] = useState("room"); // room | broadcast
  const [probing, setProbing] = useState(false);
  const [lastReason, setLastReason] = useState(null);
  const healthRef = useRef({});
  const onHealth = useCallback((h) => { healthRef.current = h; }, []);
  const [muted, setMuted] = useState(true);

  const fallbackSrc = show?.playback?.state === "live" ? show?.playback?.iframe : null;

  useEffect(() => {
    let cancelled = false;
    setTokenError(null);
    studioApi.monitorToken(showId, monitorKey).then((r) => { if (!cancelled) setToken(r.auth_token); })
      .catch((e) => { if (!cancelled) setTokenError(e?.response?.data?.detail || "Could not connect"); });
    return () => { cancelled = true; };
  }, [showId, monitorKey, tokenGen]);

  const beaconQueue = useRef([]); // event beacons that failed to send (network was down) -> flushed later
  const beacon = useCallback((extra = {}) => {
    const h = healthRef.current;
    const body = {
      monitor_id: mid, label, mode: picture === "broadcast" ? "broadcast" : h.joined ? "room" : "connecting",
      joined: !!h.joined, recv: h.recv || null, fps: h.fps ?? null, tiles: h.tiles ?? null, stalled_ms: h.stalledMs ?? null,
      attempt, ua: navigator.userAgent.slice(0, 200), ...extra,
    };
    const send = (b) => studioApi.monitorBeacon(showId, monitorKey, b);
    send(body).then(() => {
      const q = beaconQueue.current.splice(0, 10);
      q.forEach((b) => send(b).catch(() => beaconQueue.current.push(b)));
    }).catch(() => { if (extra.event && beaconQueue.current.length < 30) beaconQueue.current.push({ ...body, detail: `${body.detail || ""} [sent late]`.trim() }); });
  }, [showId, monitorKey, mid, label, picture, attempt]);
  const beaconRef = useRef(beacon);
  beaconRef.current = beacon;
  useEffect(() => { beacon(); const id = setInterval(beacon, BEACON_MS); return () => clearInterval(id); }, [beacon]);

  // A room attempt failed (link lost / transport dead / no frames / join or token error):
  // schedule a rebuild with backoff (fresh token for auth/token problems, same token otherwise).
  const onLost = useCallback((reason) => {
    setLastReason(reason);
    setAttempt((a) => a + 1);
    setPending({ reason });
  }, []);
  useEffect(() => {
    if (!pending) return undefined;
    const id = setTimeout(() => {
      setPending(null);
      beaconRef.current({ event: "reconnect", detail: `${pending.reason} (try ${attempt})` });
      if (pending.reason === "auth" || pending.reason === "token") setTokenGen((g) => g + 1); else setSession((s) => s + 1);
    }, backoff(Math.max(0, attempt - 1)));
    return () => clearTimeout(id);
  }, [pending, attempt]);
  // The room is painting again.
  const onGood = useCallback(() => {
    setAttempt(0);
    setLastReason(null);
    setPending(null);
    setPicture((p) => { if (p === "broadcast") beaconRef.current({ event: "restored", detail: "room picture back", mode: "room" }); return "room"; });
    setProbing(false);
  }, []);

  useEffect(() => { if (tokenError) onLost("token"); }, [tokenError, onLost]);

  // Repeated failures while the show is streaming -> show the broadcast instead of a black screen.
  useEffect(() => {
    if (picture === "room" && attempt >= FALLBACK_AFTER && fallbackSrc) {
      setPicture("broadcast");
      beaconRef.current({ event: "fallback", detail: `after ${attempt} failed room attempts (${lastReason || "unknown"})`, mode: "broadcast" });
    }
  }, [attempt, fallbackSrc, picture, lastReason]);
  useEffect(() => { if (picture === "broadcast" && !fallbackSrc) setPicture("room"); }, [picture, fallbackSrc]);
  // ...and keep probing the room link so we switch back as soon as it works (?probe=seconds to tune).
  useEffect(() => {
    if (picture !== "broadcast") return undefined;
    const every = Math.max(10, parseInt(new URLSearchParams(window.location.search).get("probe"), 10) || PROBE_EVERY_MS / 1000) * 1000;
    let stop = null;
    const start = () => { setProbing(true); setSession((s) => s + 1); stop = setTimeout(() => setProbing(false), PROBE_FOR_MS); };
    const id = setInterval(start, every);
    return () => { clearInterval(id); clearTimeout(stop); setProbing(false); };
  }, [picture]);

  if (show?.status === "ended") return <Notice title={show.title} text="This show has ended." testId="monitor-ended" />;
  const roomMounted = picture === "room" || probing;
  return (
    <MonitorShell show={show} picture={picture} fallbackSrc={fallbackSrc} muted={muted} setMuted={setMuted} attempt={attempt} lastReason={lastReason} tokenError={tokenError} label={label} healthRef={healthRef}>
      {roomMounted && (
        <MonitorSession
          key={`${tokenGen}:${session}`}
          show={show} token={token} session={session} muted={muted} hidden={picture === "broadcast"}
          onLost={onLost} onGood={onGood} onHealth={onHealth}
        />
      )}
    </MonitorShell>
  );
}

/** The live RealtimeKit session: renders the program, watches the picture, reports up. */
function MonitorSession({ show, token, session, muted, hidden, onLost, onGood, onHealth }) {
  const { meeting, joined, error } = useRtkMeeting(token, { audio: false, video: false, session });
  const roster = useRoster(meeting);
  const participants = show?.participants || [];
  const activeSpeaker = useStableSpeaker(meeting, roster, participants, show?.program);
  const [recv, setRecv] = useState("connected");
  const [sock, setSock] = useState("connected");
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine !== false);
  const flow = useRef({ tiles: 0, flowing: 0, at: 0 });
  const lastFramesAt = useRef(Date.now());
  const goodSince = useRef(null);
  const firedRef = useRef(false);
  const fire = useCallback((reason) => { if (!firedRef.current) { firedRef.current = true; onLost(reason); } }, [onLost]);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);
  useEffect(() => {
    if (!meeting) return undefined;
    const onLeft = ({ state } = {}) => { if (state !== "left") fire(`room ${state || "lost"}`); };
    const onMedia = ({ transport, state } = {}) => { if (!transport || transport === "recv") setRecv(state || "connected"); };
    const onSocket = ({ state } = {}) => setSock(state || "connected");
    meeting.self.on("roomLeft", onLeft);
    meeting.meta?.on?.("mediaConnectionUpdate", onMedia);
    meeting.meta?.on?.("socketConnectionUpdate", onSocket);
    return () => {
      try { meeting.self.off("roomLeft", onLeft); meeting.meta?.off?.("mediaConnectionUpdate", onMedia); meeting.meta?.off?.("socketConnectionUpdate", onSocket); } catch (e) { /* noop */ }
    };
  }, [meeting, fire]);
  useEffect(() => { if (error) fire("auth"); }, [error, fire]);
  // Link down = browser offline, RealtimeKit socket not connected, or the receive transport dead.
  const linkDown = !online || sock !== "connected" || ["failed", "disconnected"].includes(recv);
  const linkWhy = !online ? "offline" : sock !== "connected" ? `socket ${sock}` : `media ${recv}`;
  useEffect(() => {
    if (!linkDown) return undefined;
    const id = setTimeout(() => fire(linkWhy), TRANSPORT_DEAD_MS);
    return () => clearTimeout(id);
  }, [linkDown, linkWhy, fire]);

  // Frame watchdog + "good picture" detector (2 s ticks from the canvas, 1 s timer).
  const onFlow = useCallback((f) => {
    flow.current = f;
    if (f.flowing > 0) lastFramesAt.current = Date.now();
    onHealth({ joined, recv: linkDown ? linkWhy : "connected", tiles: f.tiles, fps: f.flowing > 0 ? 1 : 0, stalledMs: Date.now() - lastFramesAt.current });
  }, [joined, linkDown, linkWhy, onHealth]);
  useEffect(() => {
    if (!joined) return undefined;
    lastFramesAt.current = Date.now();
    goodSince.current = null;
    const id = setInterval(() => {
      const f = flow.current;
      const visible = document.visibilityState === "visible";
      if (!visible) { lastFramesAt.current = Date.now(); return; }
      const healthy = !linkDown && (f.tiles === 0 || f.flowing > 0);
      if (healthy) {
        if (!goodSince.current) goodSince.current = Date.now();
        else if (Date.now() - goodSince.current > 5000) onGood();
      } else {
        goodSince.current = null;
      }
      if (f.tiles > 0 && Date.now() - lastFramesAt.current > NO_FRAMES_MS) fire("no frames");
    }, 1000);
    return () => clearInterval(id);
  }, [joined, linkDown, onGood, fire]);
  useEffect(() => { onHealth({ joined, recv: linkDown ? linkWhy : "connected", tiles: flow.current.tiles, fps: null, stalledMs: null }); }, [joined, linkDown, linkWhy, onHealth]);

  return (
    <div className={hidden ? "pointer-events-none absolute inset-0 opacity-0" : "absolute inset-0"} data-testid="monitor-room-layer" data-joined={joined ? "1" : "0"} data-recv={recv} data-sock={sock}>
      <ProgramCanvas
        meeting={joined ? meeting : null}
        roster={roster}
        activeSpeaker={activeSpeaker}
        program={show?.program}
        participants={participants}
        showTitle={show?.title}
        scheduledFor={show?.scheduled_for}
        withAudio={!muted && !hidden}
        onFlow={onFlow}
        className="h-full w-full"
      />
    </div>
  );
}

/** Full-screen chrome: broadcast fallback layer, tally, status pills and the fading controls. */
function MonitorShell({ show, picture, fallbackSrc, muted, setMuted, attempt, lastReason, tokenError, label, healthRef, children }) {
  const [showUi, setShowUi] = useState(true);
  const [fs, setFs] = useState(false);
  const hideTimer = useRef(null);
  const wakeRef = useRef(null);
  const [, tick] = useState(0);
  useEffect(() => { const id = setInterval(() => tick((t) => t + 1), 2000); return () => clearInterval(id); }, []);

  // Keep the TV awake.
  useEffect(() => {
    let released = false;
    const acquire = () => {
      if (released || document.visibilityState !== "visible") return;
      navigator.wakeLock?.request?.("screen").then((l) => { if (released) l.release(); else wakeRef.current = l; }).catch(() => {});
    };
    acquire();
    document.addEventListener("visibilitychange", acquire);
    return () => { released = true; document.removeEventListener("visibilitychange", acquire); wakeRef.current?.release?.(); };
  }, []);

  // Controls fade out after 3 s without pointer activity.
  const poke = useCallback(() => {
    setShowUi(true);
    document.body.style.cursor = "";
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => { setShowUi(false); document.body.style.cursor = "none"; }, 3000);
  }, []);
  useEffect(() => { poke(); return () => clearTimeout(hideTimer.current); }, [poke]);
  useEffect(() => {
    const onFs = () => setFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);
  const toggleFs = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else document.documentElement.requestFullscreen?.().catch(() => {});
  };
  const [tally, setTally] = useState(() => localStorage.getItem("fc_monitor_tally") !== "0");
  useEffect(() => { localStorage.setItem("fc_monitor_tally", tally ? "1" : "0"); }, [tally]);
  // The broadcast player doesn't recover from a network blip on its own -> reload it when we're back online.
  const [iframeGen, setIframeGen] = useState(0);
  useEffect(() => {
    const up = () => setIframeGen((g) => g + 1);
    window.addEventListener("online", up);
    return () => window.removeEventListener("online", up);
  }, []);

  const h = healthRef.current || {};
  const broadcast = picture === "broadcast";
  const starving = !broadcast && h.joined && h.tiles > 0 && (h.stalledMs || 0) > 4000;
  const reconnecting = !broadcast && (!!tokenError || attempt > 0 || !h.joined || (h.recv && h.recv !== "connected"));
  const status = broadcast ? "Showing the public broadcast (delayed)"
    : tokenError ? `Can't reach the room: ${tokenError}`
      : !h.joined ? (attempt > 0 ? `Reconnecting… (try ${attempt}${lastReason ? ` · ${lastReason}` : ""})` : "Connecting…")
        : starving ? `No picture from the room for ${Math.round((h.stalledMs || 0) / 1000)} s — weak connection on this screen`
          : "Live from the room · ~0.3 s behind";

  const src = useMemo(() => {
    if (!fallbackSrc) return null;
    const u = new URL(fallbackSrc);
    u.searchParams.set("autoplay", "true");
    u.searchParams.set("muted", muted ? "true" : "false");
    u.searchParams.set("preload", "auto");
    u.searchParams.set("controls", "false");
    return u.toString();
  }, [fallbackSrc, muted]);

  return (
    <div className="fixed inset-0 bg-black" onPointerMove={poke} onPointerDown={poke} data-testid="room-monitor" data-picture={picture}>
      {broadcast && src && (
        <iframe key={`${src}|${iframeGen}`} src={src} title="Broadcast" className="absolute inset-0 h-full w-full" allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;" data-testid="monitor-broadcast-iframe" />
      )}
      {children}
      {tally && <Tally show={show} />}
      {(broadcast || reconnecting || starving) && (
        <div className={`absolute left-1/2 top-6 z-10 -translate-x-1/2 rounded-full px-4 py-2 text-sm backdrop-blur ${broadcast ? "bg-black/70 text-sky-200" : "bg-black/70 text-amber-200"}`} data-testid={broadcast ? "monitor-broadcast-pill" : "monitor-reconnecting"}>
          {broadcast ? <Tv className="mr-2 inline h-4 w-4" /> : <WifiOff className="mr-2 inline h-4 w-4" />}
          {broadcast ? "Room link too weak on this screen — showing the public broadcast (a few seconds behind). Retrying the room link…" : status}
        </div>
      )}
      <div className={`absolute inset-x-0 bottom-0 z-10 flex items-end justify-between gap-4 bg-gradient-to-t from-black/80 to-transparent p-5 transition-opacity duration-500 ${showUi ? "opacity-100" : "pointer-events-none opacity-0"}`} data-testid="monitor-controls">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-white/50">Room monitor · {label}</p>
          <p className="truncate text-lg font-semibold text-white">{show?.title}</p>
          <p className="text-xs text-white/60" data-testid="monitor-status">{status}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button type="button" onClick={() => setTally((t) => !t)} className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-2.5 text-sm text-white backdrop-blur hover:bg-white/25" title={tally ? "Hide the ON AIR / REC badges and clock" : "Show the ON AIR / REC badges and clock"} data-testid="monitor-tally-toggle">
            {tally ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />} Badges
          </button>
          <button type="button" onClick={() => setMuted((m) => !m)} className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2.5 text-sm font-medium text-white backdrop-blur hover:bg-white/25" title={muted ? "Sound is off (the room already hears itself)" : "Mute"} data-testid="monitor-mute">
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />} {muted ? "Sound off" : "Sound on"}
          </button>
          <button type="button" onClick={toggleFs} className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-2.5 text-sm text-white backdrop-blur hover:bg-white/25" title={fs ? "Exit full screen" : "Full screen"} data-testid="monitor-fullscreen">
            {fs ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
